// Static blog data for ClariDent RCM (no backend). Full article content baked in.
export const posts = [
  {
    "slug": "general-dentistry-billing-fundamentals",
    "title": "General Dentistry Billing: The Fundamentals That Protect Your Revenue",
    "excerpt": "Hygiene frequencies, downgrades, and same-day rules — general dentistry billing is won in the boring details. Master these and the revenue follows.",
    "category": "Specialty RCM",
    "read_time": "5 min read",
    "cover": "/blog/spec-general.jpg",
    "published_at": "2026-08-21",
    "content": [
      {
        "type": "p",
        "text": "General practices run on hundreds of small, routine claims — which means small, routine errors compound into real revenue loss. The fundamentals below are where general dentistry billing is actually won or lost."
      },
      {
        "type": "h2",
        "text": "Recall frequency rules are three different rules"
      },
      {
        "type": "p",
        "text": "\"Twice a year,\" \"two per 12 months,\" and \"once every six months\" are not the same benefit. The second and third deny claims based on exact dates, not calendar years. Your verification workflow needs each patient's last service dates checked against their specific plan's rule — every single recall visit."
      },
      {
        "type": "h2",
        "text": "The composite downgrade"
      },
      {
        "type": "p",
        "text": "Many plans pay posterior composites at the amalgam rate. That's a plan design choice, not a denial — but if your treatment estimates don't account for it, the patient gets a surprise balance and your front desk gets a difficult conversation. Quote with the downgrade built in."
      },
      {
        "type": "h2",
        "text": "Same-day procedures need care"
      },
      {
        "type": "list",
        "items": [
          "Multiple restorations on the same tooth, or exam codes billed with certain procedures, can bundle or deny depending on payer rules.",
          "X-rays taken the same day as other imaging can hit frequency conflicts.",
          "When in doubt, document why each procedure was separately necessary."
        ]
      },
      {
        "type": "h2",
        "text": "Hygiene AR is still AR"
      },
      {
        "type": "p",
        "text": "Small balances feel harmless individually and significant in aggregate. A general practice's patient-AR leak is usually a hundred small balances, not one big one — which is why consistent statement cadence matters more than occasional collection pushes."
      },
      {
        "type": "p",
        "text": "This is the daily work ClariDent was built for: verification before the visit, clean claims within 24 hours, and steady follow-up until every balance resolves."
      }
    ],
    "seo_title": "General Dentistry Billing Fundamentals | ClariDent RCM",
    "seo_description": "General dentistry billing fundamentals: recall frequency rules, composite downgrades, same-day procedure billing, and verification habits that protect revenue."
  },
  {
    "slug": "prostho-billing-high-value-claims",
    "title": "Prosthodontic Billing: High-Value Claims Need High-Detail Documentation",
    "excerpt": "Crowns, bridges, dentures, and implants carry the biggest ticket sizes in dentistry — and the strictest payer scrutiny. Here's how to bill them so they pay.",
    "category": "Specialty RCM",
    "read_time": "5 min read",
    "cover": "/blog/spec-prostho.jpg",
    "published_at": "2026-08-20",
    "content": [
      {
        "type": "p",
        "text": "A single prostho case can be worth more than a week of hygiene. Payers know it too — which is why crowns, bridges, dentures, and implants face the toughest documentation demands in dental billing."
      },
      {
        "type": "h2",
        "text": "Replacement clauses catch everyone once"
      },
      {
        "type": "p",
        "text": "Most plans won't pay to replace a crown or denture within five to ten years of the original placement. Verifying the replacement history before treatment planning prevents the worst kind of surprise: a completed case the plan won't cover. If the existing prosthesis is failing early, the narrative must say why — fractures, decay at margins, or non-restorable tooth structure."
      },
      {
        "type": "h2",
        "text": "Prep date vs. seat date"
      },
      {
        "type": "p",
        "text": "Some payers want the claim at preparation, others at seating — and billing the wrong date can void coverage if the patient's plan changes between visits. Know each payer's rule and bill accordingly."
      },
      {
        "type": "h2",
        "text": "Narratives that actually get read"
      },
      {
        "type": "list",
        "items": [
          "State the diagnosis: fracture, caries extent, missing tooth, failed existing restoration",
          "Attach current radiographs and intraoral photos where they strengthen the case",
          "For implants, document bone status and the restorative plan — medical-dental crossover may apply in trauma or pathology cases",
          "Keep lab invoices organized; payers occasionally request them"
        ]
      },
      {
        "type": "h2",
        "text": "Watch the downgrades"
      },
      {
        "type": "p",
        "text": "Alternate benefit clauses let payers reimburse a porcelain crown at the metal-crown rate, or an implant at the bridge rate. Know which plans downgrade before you quote the patient — your estimate, and your reviews, depend on it."
      },
      {
        "type": "p",
        "text": "High-value claims deserve specialist handling. Our verification and denial workflows are built for exactly this level of detail."
      }
    ],
    "seo_title": "Prosthodontic Billing: Crown, Denture & Implant Claims | ClariDent RCM",
    "seo_description": "Prosthodontic billing guidance: replacement clauses, prep vs seat dates, narratives for crowns and dentures, and implant claim documentation."
  },
  {
    "slug": "endo-billing-root-canal-claims",
    "title": "Endodontic Billing: Getting Root Canal Claims Right",
    "excerpt": "Anterior, bicuspid, or molar? Initial treatment or retreatment? Endo claims live and die on precision — here's where they go wrong.",
    "category": "Specialty RCM",
    "read_time": "4 min read",
    "cover": "/blog/spec-endo.jpg",
    "published_at": "2026-08-19",
    "content": [
      {
        "type": "p",
        "text": "Endodontic procedures are high-value, single-visit treatments — which makes every denied claim sting. The good news is that endo denials cluster around a short list of precision errors that are entirely preventable."
      },
      {
        "type": "h2",
        "text": "Tooth-specific coding is non-negotiable"
      },
      {
        "type": "p",
        "text": "Root canal codes differ for anterior teeth, bicuspids, and molars — and the fee difference is significant. The claim must carry the correct tooth number and the matching code. A transposed digit doesn't just delay payment; it can trigger a records request."
      },
      {
        "type": "h2",
        "text": "Initial therapy vs. retreatment"
      },
      {
        "type": "p",
        "text": "Retreatment codes pay differently than initial therapy, and payers ask harder questions. Retreatment claims should include the history — when the original treatment was done and why it failed — right in the narrative, before the payer has to ask."
      },
      {
        "type": "h2",
        "text": "Buildups and imaging"
      },
      {
        "type": "list",
        "items": [
          "A core buildup is a separate, billable procedure — leaving it off the claim is giving away revenue on most molar cases.",
          "Pre-operative and post-operative radiographs belong on the claim; payers increasingly expect both.",
          "If the crown is done elsewhere, document the referral so the endo claim stands alone cleanly."
        ]
      },
      {
        "type": "h2",
        "text": "The completion-date trap"
      },
      {
        "type": "p",
        "text": "Multi-visit endo billed at the start date instead of the completion date is a classic denial trigger. Bill on the date treatment is completed, and make sure the chart supports it."
      },
      {
        "type": "p",
        "text": "Precision is the whole game in endo billing — and it's the standard our claim preparation and tracking workflows are built around."
      }
    ],
    "seo_title": "Endodontic Billing: Root Canal Claims Done Right | ClariDent RCM",
    "seo_description": "Endodontic billing essentials: tooth-specific codes, retreatment vs initial therapy, buildups billed separately, and the imaging payers require."
  },
  {
    "slug": "perio-billing-documentation-srp-claims",
    "title": "Periodontal Billing: The Documentation That Gets SRP Claims Paid",
    "excerpt": "Scaling and root planing is one of the most denied procedures in dentistry — usually for missing documentation, not missing coverage. Here's the checklist that fixes it.",
    "category": "Specialty RCM",
    "read_time": "5 min read",
    "cover": "/blog/spec-perio.jpg",
    "published_at": "2026-08-18",
    "content": [
      {
        "type": "p",
        "text": "Ask a perio office which procedure generates the most denials and the answer is almost always scaling and root planing. The frustrating part: most of those denials aren't about coverage. They're about documentation that never made it onto the claim."
      },
      {
        "type": "h2",
        "text": "What payers want to see"
      },
      {
        "type": "list",
        "items": [
          "A current full-mouth perio chart with pocket depths, typically within the last 12 months",
          "Radiographs showing bone loss consistent with the diagnosis",
          "Quadrant-level detail — which teeth, which quadrants, how many teeth per quadrant",
          "A narrative connecting the charting and imaging to the treatment plan"
        ]
      },
      {
        "type": "h2",
        "text": "The four-teeth-per-quadrant rule"
      },
      {
        "type": "p",
        "text": "SRP codes distinguish between quadrants with one to three teeth and quadrants with four or more. Bill the wrong variant and the claim pays less — or denies. It sounds minor; across a full SRP schedule, it's real money."
      },
      {
        "type": "h2",
        "text": "Perio maintenance is not a prophy"
      },
      {
        "type": "p",
        "text": "After active therapy, perio maintenance codes apply — and they carry their own frequency rules that differ from prophylaxis. Billing maintenance as a prophy (or vice versa) creates denials, downgrades, and patient balance confusion. Your recall system and your billing codes have to agree."
      },
      {
        "type": "h2",
        "text": "Build the attachment habit"
      },
      {
        "type": "p",
        "text": "Every SRP claim should leave with the chart and radiographs attached — automatically, not when someone remembers. Practices that make attachments part of the claim template see SRP denials drop dramatically."
      },
      {
        "type": "p",
        "text": "Our billing workflows treat documentation as part of claim preparation, and our denial team knows exactly which evidence overturns a perio denial."
      }
    ],
    "seo_title": "Periodontal Billing: SRP Documentation Checklist | ClariDent RCM",
    "seo_description": "Why scaling and root planing claims get denied and the exact documentation — perio charting, pocket depths, x-rays, narratives — that gets perio claims paid."
  },
  {
    "slug": "pediatric-dental-billing-guide",
    "title": "Pediatric Dental Billing: What Every Pedo Practice Should Watch",
    "excerpt": "Age-based frequency limits, sealant rules, and family accounts make pediatric billing its own discipline. Here's what keeps pedo claims clean.",
    "category": "Specialty RCM",
    "read_time": "4 min read",
    "cover": "/blog/spec-pediatric.jpg",
    "published_at": "2026-08-17",
    "content": [
      {
        "type": "p",
        "text": "Pediatric practices run on volume, prevention, and families — and each of those creates its own billing trap. The practices that collect well are the ones whose verification catches age-based rules before the appointment."
      },
      {
        "type": "h2",
        "text": "Age changes everything"
      },
      {
        "type": "p",
        "text": "Fluoride varnish, sealants, and space maintainers all carry age-based coverage rules that vary by payer. A sealant covered at age 9 may be excluded at 15. Verification needs to check the specific procedure against the patient's age on the date of service — a detail generic verification workflows often miss."
      },
      {
        "type": "h2",
        "text": "Primary vs. permanent teeth matter to payers"
      },
      {
        "type": "p",
        "text": "Restorative codes and coverage can differ depending on whether the tooth is primary or permanent. Claims with the wrong tooth designation deny or underpay, and they're annoying to appeal after the fact. Clean charting upstream is the fix."
      },
      {
        "type": "h2",
        "text": "Family accounts and the guarantor problem"
      },
      {
        "type": "p",
        "text": "Three siblings seen on the same afternoon means one statement to the right guarantor — not three confusing balances. Patient billing in pedo is really household billing: consolidated statements, clear family balances, and reminders timed to parents, not patients."
      },
      {
        "type": "h2",
        "text": "Frequency limits drive recare revenue"
      },
      {
        "type": "p",
        "text": "Six-month recalls collide with plans that pay exams twice per year, twice per 12 months, or once per six months — three different rules that look identical until a claim denies. Tracking exact last-service dates per payer rule keeps recare claims clean."
      },
      {
        "type": "p",
        "text": "ClariDent verifies schedules in advance with age- and frequency-specific checks, and runs patient statements on a family-friendly cadence that parents actually respond to."
      }
    ],
    "seo_title": "Pediatric Dental Billing Guide for Pedo Practices | ClariDent RCM",
    "seo_description": "Pediatric dental billing essentials: age-based frequency limitations, fluoride and sealant coverage rules, primary vs permanent tooth coding, and family account billing."
  },
  {
    "slug": "oral-surgery-billing-medical-dental-crossover",
    "title": "Oral Surgery Billing: The Medical-Dental Crossover Explained",
    "excerpt": "Some oral surgery procedures can be billed to medical insurance — and knowing which ones, with what documentation, is where most practices leave money behind.",
    "category": "Specialty RCM",
    "read_time": "5 min read",
    "cover": "/blog/spec-oral-surgery.jpg",
    "published_at": "2026-08-16",
    "content": [
      {
        "type": "p",
        "text": "Oral surgery sits on the border between dentistry and medicine — and that border is exactly where revenue gets lost. Certain procedures may be payable under a patient's medical plan, but only when the claim is built for medical review from the start."
      },
      {
        "type": "h2",
        "text": "When medical insurance may apply"
      },
      {
        "type": "p",
        "text": "Trauma, pathology, biopsies, certain infections, and medically necessary extractions can qualify for medical coverage. The key is diagnosis-driven billing: the medical claim needs the supporting diagnosis and documentation, not just a procedure code. Every case should be screened for medical necessity before defaulting to the dental plan."
      },
      {
        "type": "h2",
        "text": "Anesthesia and sedation need their own attention"
      },
      {
        "type": "p",
        "text": "Sedation is frequently denied not because it isn't covered, but because the claim lacks the anesthesia record, time documentation, or the narrative explaining why it was necessary. Build the anesthesia documentation habit into the operative workflow, not the billing workflow."
      },
      {
        "type": "h2",
        "text": "Documentation payers actually look for"
      },
      {
        "type": "list",
        "items": [
          "Operative notes describing the procedure, findings, and complications",
          "Pre- and post-operative imaging where relevant",
          "Pre-authorization records for procedures that require them",
          "Clear narratives linking diagnosis to treatment"
        ]
      },
      {
        "type": "h2",
        "text": "Don't forget the dental side"
      },
      {
        "type": "p",
        "text": "When medical denies or pays partially, the dental plan may still respond — but only if the claim is submitted correctly with the EOB attached. Crossover coordination is a workflow, not an afterthought."
      },
      {
        "type": "p",
        "text": "This crossover is exactly where a specialist billing team earns its keep. Our denial management and AR follow-up teams work surgical claims with the documentation discipline they demand."
      }
    ],
    "seo_title": "Oral Surgery Billing: Medical-Dental Crossover Explained | ClariDent RCM",
    "seo_description": "Oral surgery billing guidance: when to bill medical vs dental insurance, anesthesia documentation, pre-authorization, and the op notes payers expect."
  },
  {
    "slug": "ortho-billing-what-makes-it-different",
    "title": "Orthodontic Billing: What Makes It Different",
    "excerpt": "Ortho claims don't work like general dentistry claims. Lifetime maximums, banding dates, and continuation claims change everything about how you bill and follow up.",
    "category": "Specialty RCM",
    "read_time": "5 min read",
    "cover": "/blog/spec-ortho.jpg",
    "published_at": "2026-08-15",
    "content": [
      {
        "type": "p",
        "text": "Orthodontic billing breaks the rules most billers learn in general dentistry. Treatment spans months or years, benefits follow completely different structures, and a single case can involve a dozen claims over its lifetime. Practices that bill ortho like restorative dentistry leak revenue at every stage."
      },
      {
        "type": "h2",
        "text": "Lifetime maximums, not annual ones"
      },
      {
        "type": "p",
        "text": "Orthodontic benefits usually come with a lifetime maximum that sits completely separate from the patient's annual dental maximum. Verifying both — and tracking how much of the lifetime benefit remains — has to happen before the banding appointment, not after the first denial."
      },
      {
        "type": "h2",
        "text": "Banding claims and continuation claims"
      },
      {
        "type": "p",
        "text": "The initial claim goes out when treatment starts (the banding date), and payers then reimburse over the course of treatment through periodic or continuation claims. Miss a continuation submission window and payments pause. Miss several, and you're chasing an aging balance on a case that's almost finished."
      },
      {
        "type": "h2",
        "text": "Age limitations and coverage traps"
      },
      {
        "type": "list",
        "items": [
          "Many plans cover orthodontics only up to a certain age — adult ortho is frequently excluded entirely.",
          "Some payers require the banding date to fall within the coverage period, so a plan change mid-treatment can cut off reimbursement.",
          "Dual coverage needs coordination of benefits handled correctly, or secondary payments stall."
        ]
      },
      {
        "type": "h2",
        "text": "The patient portion runs on installments"
      },
      {
        "type": "p",
        "text": "Ortho is one of the few specialties where patient balances are routinely financed in-house over 12–24 months. That means your patient billing workflow needs scheduled statements and consistent follow-up for the entire length of treatment — long after the clinical excitement has faded."
      },
      {
        "type": "p",
        "text": "ClariDent's verification, claim tracking, and patient statement workflows are built to handle exactly this rhythm — from the banding claim to the final installment."
      }
    ],
    "seo_title": "Orthodontic Billing: What Makes It Different | ClariDent RCM",
    "seo_description": "Orthodontic billing explained: lifetime maximums, banding and continuation claims, age limitations, and patient installment workflows for ortho practices."
  },
  {
    "slug": "7-dental-billing-mistakes-costing-revenue",
    "title": "7 Dental Billing Mistakes That Can Cost Your Practice Revenue",
    "excerpt": "Most practices don't lose revenue in one big leak — they lose it in a dozen small ones. Here are the seven billing mistakes we see most often, and the fixes that actually stick.",
    "category": "Billing Tips",
    "read_time": "6 min read",
    "cover": "/blog/blog-billing-mistakes.jpg",
    "published_at": "2026-08-12",
    "content": [
      {
        "type": "p",
        "text": "Most practices don't lose revenue in one big leak. They lose it in a dozen small ones: a claim that went out without an x-ray, a denial nobody appealed, a patient balance that quietly aged past 90 days. Individually they look minor. Together, they decide whether a good clinical month actually feels like one when you open the books."
      },
      {
        "type": "p",
        "text": "After years of working inside dental billing, these are the seven mistakes we see most often — and the fixes that actually stick."
      },
      {
        "type": "h2",
        "text": "1. Skipping insurance verification before the appointment"
      },
      {
        "type": "p",
        "text": "When coverage isn't confirmed before treatment, everything downstream gets harder: claims deny for eligibility, estimates are wrong, and patients get surprise balances they never budgeted for. Verifying the schedule two to three days ahead prevents most of it."
      },
      {
        "type": "h2",
        "text": "2. Submitting claims with outdated CDT codes"
      },
      {
        "type": "p",
        "text": "CDT codes change every year. A claim built on last year's cheat sheet can deny, underpay, or create a compliance flag. Someone — on your team or at your billing partner — must own code updates the day they are released."
      },
      {
        "type": "h2",
        "text": "3. Sending claims without the required attachments"
      },
      {
        "type": "p",
        "text": "Crowns, scaling and root planing, and many surgical procedures need narratives, x-rays, or perio charts. A claim without them isn't delayed — it's denied. Build an attachment checklist by procedure type and don't let a claim leave without it."
      },
      {
        "type": "h2",
        "text": "4. Treating denials as write-offs"
      },
      {
        "type": "p",
        "text": "A large share of denied claims are recoverable with a corrected claim or a documented appeal. If your process stops at 'the payer said no,' you're donating revenue. Every denial needs a reason code, an owner, and a follow-up date."
      },
      {
        "type": "h2",
        "text": "5. Inconsistent claim submission timing"
      },
      {
        "type": "p",
        "text": "Claims that go out in weekly batches age before anyone even looks at them. Daily submission — within 24 hours of the date of service — is the simplest cash-flow improvement most practices can make."
      },
      {
        "type": "h2",
        "text": "6. Letting patient balances age silently"
      },
      {
        "type": "p",
        "text": "Statements that go out late — or never — quietly train patients not to pay on time. A consistent cadence of statement, reminder, and personal outreach collects more and keeps the relationship intact."
      },
      {
        "type": "h2",
        "text": "7. Never reading the AR aging report"
      },
      {
        "type": "p",
        "text": "Your aging report is the earliest warning system your practice has. Reviewed weekly, it shows which payers are slowing down and which balances need attention. Ignored, it becomes a museum of money you used to be owed."
      },
      {
        "type": "h2",
        "text": "The pattern behind all seven"
      },
      {
        "type": "p",
        "text": "None of these fixes require new software or new hires. They require a consistent process owned by someone whose job is billing — not someone squeezing it in between patients. Whether that person sits at your front desk or on a partner's team is a business decision. Whether the process exists is not."
      }
    ],
    "seo_title": "7 Dental Billing Mistakes That Cost Practices Revenue | ClariDent RCM",
    "seo_description": "The seven most common dental billing mistakes — from skipped insurance verification to ignored denials — and the practical fixes that protect your practice revenue."
  },
  {
    "slug": "dental-ar-aging-explained",
    "title": "Dental AR Aging Explained: What Every Practice Should Watch",
    "excerpt": "Your AR aging report is the earliest warning system your practice has. Here's how to read the buckets, why 90+ is the danger zone, and the weekly routine that keeps it healthy.",
    "category": "AR & Collections",
    "read_time": "5 min read",
    "cover": "/blog/blog-ar-aging.jpg",
    "published_at": "2026-08-05",
    "content": [
      {
        "type": "p",
        "text": "If you want one report that predicts financial stress in a dental practice, it's the AR aging report. Production tells you what you earned. AR aging tells you what you've actually collected — and how long money has been sitting uncollected."
      },
      {
        "type": "h2",
        "text": "The four buckets, in plain English"
      },
      {
        "type": "list",
        "items": [
          "0–30 days: fresh claims and balances. Normal, healthy — this is where most of your AR should live.",
          "31–60 days: follow-up territory. Claims here need a status call or portal check, not patience.",
          "61–90 days: warning zone. Something went wrong — a denial nobody saw, a missing attachment, a payer stalling.",
          "90+ days: the danger zone. Collectability drops sharply with every additional week, and timely-filing limits start closing doors."
        ]
      },
      {
        "type": "h2",
        "text": "Separate insurance AR from patient AR"
      },
      {
        "type": "p",
        "text": "These two buckets behave completely differently and need different workflows. Insurance AR responds to claim status checks, corrected claims, and appeals. Patient AR responds to clear statements, timely reminders, and respectful personal outreach. Mixing them in one report hides which machine is actually broken."
      },
      {
        "type": "h2",
        "text": "A weekly 30-minute AR routine"
      },
      {
        "type": "p",
        "text": "Work the newest claims first — they're the easiest to fix and the fastest to pay. Then work the oldest bucket by dollar value, largest balance first. Every account you touch gets a note and a next action date. No account should ever sit untouched for more than 30 days."
      },
      {
        "type": "h2",
        "text": "When to get help"
      },
      {
        "type": "p",
        "text": "If your 90+ bucket grows two months in a row, the issue isn't effort — it's capacity or process. That's the point where a dedicated AR follow-up team pays for itself, because every week of delay quietly converts receivables into write-offs. A free RCM audit will show you exactly how much is recoverable before you decide anything."
      }
    ],
    "seo_title": "Dental AR Aging Explained: Buckets, Benchmarks & Fixes | ClariDent RCM",
    "seo_description": "What dental AR aging buckets mean, why the 90+ day bucket is dangerous, and the weekly follow-up workflow that keeps accounts receivable healthy."
  },
  {
    "slug": "why-dental-claims-get-denied",
    "title": "Why Dental Claims Get Denied — And How to Prevent Them",
    "excerpt": "Most denials trace back to a handful of preventable causes. Here's what actually triggers them, how to stop them before submission, and how to fight the ones that slip through.",
    "category": "Denial Management",
    "read_time": "5 min read",
    "cover": "/blog/blog-denials.jpg",
    "published_at": "2026-07-29",
    "content": [
      {
        "type": "p",
        "text": "A denial isn't a verdict — it's a delay with a reason code attached. And here's the part most practices underestimate: the large majority of dental denials trace back to a handful of preventable causes that happened before the claim was ever submitted."
      },
      {
        "type": "h2",
        "text": "The causes behind most denials"
      },
      {
        "type": "list",
        "items": [
          "Eligibility: coverage terminated, plan changed, or the patient was never verified for that date of service.",
          "Patient data mismatches: legal name, date of birth, or subscriber ID doesn't match the payer's record exactly.",
          "Missing documentation: no x-ray, perio chart, or narrative for procedures that require them.",
          "Frequency and limitation rules: the plan covers the procedure — just not yet, or not the way it was billed.",
          "Coding errors: outdated CDT codes, wrong tooth numbers, or missing quadrants.",
          "Timely filing: the claim simply went out too late, or a corrected claim missed the appeal window."
        ]
      },
      {
        "type": "h2",
        "text": "Prevention beats appeals"
      },
      {
        "type": "p",
        "text": "The cheapest denial is the one that never happens. Three habits prevent most of them: verify every appointment before the date of service, run every claim through a scrubbing checklist (demographics, codes, attachments), and submit within 24 hours so there's always time to correct and resubmit inside the filing window."
      },
      {
        "type": "h2",
        "text": "For the denials that slip through"
      },
      {
        "type": "p",
        "text": "Work every denial the week it arrives. Read the reason code, fix what's fixable, and appeal with documentation — clinical notes, images, and a narrative written to the payer's own policy language. Track your denial rate and top three reason codes monthly; if the same cause appears twice, it's a process problem, not bad luck."
      },
      {
        "type": "p",
        "text": "If your team doesn't have the hours to work denials systematically, that's exactly the gap our denial management service fills — identify, resolve, prevent, improve."
      }
    ],
    "seo_title": "Why Dental Claims Get Denied & How to Prevent Them | ClariDent RCM",
    "seo_description": "The real reasons dental claims get denied — eligibility, documentation, coding, frequency limits, timely filing — plus a prevention workflow and appeal basics."
  },
  {
    "slug": "dental-insurance-verification-before-treatment",
    "title": "Dental Insurance Verification: Why It Matters Before Treatment",
    "excerpt": "Verification takes minutes before the appointment. Skipping it costs weeks of rework after it. What to capture, when to verify, and why it protects both revenue and relationships.",
    "category": "Insurance Verification",
    "read_time": "4 min read",
    "cover": "/blog/blog-verification.jpg",
    "published_at": "2026-07-22",
    "content": [
      {
        "type": "p",
        "text": "Ask an experienced dental biller where denials begin and you'll hear the same answer: at the appointment that was never verified. Insurance verification is five minutes of prevention that saves weeks of cure."
      },
      {
        "type": "h2",
        "text": "Eligibility check vs. full breakdown"
      },
      {
        "type": "p",
        "text": "An eligibility check confirms the patient is actively covered. A full breakdown goes further — and it's what your treatment plan estimates actually depend on:"
      },
      {
        "type": "list",
        "items": [
          "Plan status, effective dates, and waiting periods",
          "Annual maximum, remaining benefits, and deductible status",
          "Coverage percentages by procedure category",
          "Frequency limitations (exams, x-rays, cleanings, crown replacement years)",
          "Exclusions and missing-tooth clauses",
          "Coordination of benefits when a patient has dual coverage"
        ]
      },
      {
        "type": "h2",
        "text": "The right cadence"
      },
      {
        "type": "p",
        "text": "Verify the schedule two to three days ahead so there's time to chase missing information. Do a full breakdown for new patients, new plan years, and any patient whose plan changed. Save the breakdown or eligibility printout in the patient's document center — when a payer disputes a claim later, that record is your evidence."
      },
      {
        "type": "h2",
        "text": "It's a patient-experience tool, not just a billing one"
      },
      {
        "type": "p",
        "text": "When patients know their portion before treatment, they pay it — usually at the time of service. When they learn it from a statement six weeks later, you get an awkward phone call and an aging balance. Verification is where good collections actually start."
      },
      {
        "type": "p",
        "text": "ClariDent verifies your schedule in advance and files every breakdown in your document center — priced per verification, so you pay for exactly what you use."
      }
    ],
    "seo_title": "Dental Insurance Verification: Why It Matters Before Treatment | ClariDent RCM",
    "seo_description": "What proper dental insurance verification captures, when to verify the schedule, and how it prevents denials and surprise patient balances."
  },
  {
    "slug": "better-dental-rcm-improve-cash-flow",
    "title": "How Better Dental RCM Can Improve Practice Cash Flow",
    "excerpt": "Cash flow is speed times completeness: how fast money moves and how much of it actually arrives. Five operational levers move both — and none of them require more production.",
    "category": "Practice Growth",
    "read_time": "5 min read",
    "cover": "/blog/blog-cashflow.jpg",
    "published_at": "2026-07-15",
    "content": [
      {
        "type": "p",
        "text": "Two practices can produce the same dentistry and live in completely different financial realities. The difference is rarely clinical. It's how fast money moves through the revenue cycle — and how much of it actually arrives."
      },
      {
        "type": "h2",
        "text": "Lever 1: Submit claims within 24 hours"
      },
      {
        "type": "p",
        "text": "Every day a claim waits is a day added to when you get paid — and a day closer to timely-filing limits. Daily submission is the single simplest cash-flow improvement available to any practice."
      },
      {
        "type": "h2",
        "text": "Lever 2: Raise your first-pass acceptance rate"
      },
      {
        "type": "p",
        "text": "A claim that pays on first submission costs minutes. A claim that denies costs weeks of rework. Verification, claim scrubbing, and procedure-specific attachment checklists are what move first-pass acceptance — and they're process, not talent."
      },
      {
        "type": "h2",
        "text": "Lever 3: Post payments daily"
      },
      {
        "type": "p",
        "text": "Unposted EOBs and EFTs hide denials, underpayments, and patient balances. Daily posting means problems surface the day they happen, and your reports reflect reality instead of last month."
      },
      {
        "type": "h2",
        "text": "Lever 4: Work AR on a weekly cadence"
      },
      {
        "type": "p",
        "text": "Thirty focused minutes a week — newest claims first, then the oldest bucket by dollar value — keeps the 90+ bucket from ever forming. Consistency matters more than intensity."
      },
      {
        "type": "h2",
        "text": "Lever 5: Collect the patient portion at time of service"
      },
      {
        "type": "p",
        "text": "This only works when verification gave you an accurate estimate before the appointment. The practices that collect well at the front desk are the ones whose verification workflow is solid behind it."
      },
      {
        "type": "h2",
        "text": "Measure it"
      },
      {
        "type": "p",
        "text": "Three numbers tell the story: days in AR, net collection rate, and denial rate. Review them monthly, and review the aging buckets weekly. Predictable cash flow isn't a finance trick — it's what makes payroll calm, equipment decisions easy, and growth fundable."
      }
    ],
    "seo_title": "How Better Dental RCM Improves Practice Cash Flow | ClariDent RCM",
    "seo_description": "Five operational levers — faster claim submission, clean claims, daily posting, weekly AR cadence, and time-of-service collection — that improve dental practice cash flow."
  },
  {
    "slug": "in-house-vs-outsourced-dental-rcm",
    "title": "In-House Billing vs. Outsourced Dental RCM: Which Is Right for Your Practice?",
    "excerpt": "An honest comparison: what in-house billing really costs, what outsourcing actually changes, what you keep control of, and how to decide which model fits your practice.",
    "category": "Practice Growth",
    "read_time": "6 min read",
    "cover": "/blog/blog-inhouse-outsource.jpg",
    "published_at": "2026-07-08",
    "content": [
      {
        "type": "p",
        "text": "This is a real decision with real trade-offs, and anyone who tells you one model is always right is selling something. Here's the honest version."
      },
      {
        "type": "h2",
        "text": "What in-house billing actually costs"
      },
      {
        "type": "p",
        "text": "The salary is the visible part. Add benefits, payroll taxes, training, billing software, and the management time it takes to supervise the work. Then add the risk most practices forget: your entire revenue cycle sits with one or two people. Vacation, sick leave, or a resignation doesn't pause your claims — it piles them up."
      },
      {
        "type": "h2",
        "text": "What outsourcing actually changes"
      },
      {
        "type": "list",
        "items": [
          "Specialization: a dental-only billing team works claims all day, every day, and tracks CDT and payer policy changes as its core job.",
          "Continuity: your billing doesn't stop when one person is out — a team carries it.",
          "Aligned pricing: percentage-of-collections means your billing partner earns more only when you collect more.",
          "Front desk relief: your team gets hours back for scheduling, patient experience, and time-of-service collection."
        ]
      },
      {
        "type": "h2",
        "text": "What you keep control of"
      },
      {
        "type": "p",
        "text": "Everything clinical and relational stays in-house: treatment planning, scheduling, fees, and patient communication about care. A good RCM partner works inside your practice management software with transparent reporting, so you can see every claim, payment, and outstanding balance — not a monthly black box."
      },
      {
        "type": "h2",
        "text": "When in-house still makes sense"
      },
      {
        "type": "p",
        "text": "If you have a stable, experienced billing lead, a denial rate you're proud of, a 90+ bucket that's nearly empty, and coverage planned for absences — in-house can absolutely work. The question worth asking is what it costs you per collected dollar, and what happens to collections the month that person leaves."
      },
      {
        "type": "h2",
        "text": "How to evaluate a partner"
      },
      {
        "type": "p",
        "text": "Ask four things of any billing company: Are they dental-only? Can they show transparent, on-demand reporting? Do they work inside your existing practice management software? And is the pricing model one you can model against your own collections before signing? If the answer to any of these is vague, keep looking."
      },
      {
        "type": "p",
        "text": "The lowest-risk way to decide: get a free RCM audit first. When you can see your own denial rate, AR aging, and recoverable revenue in black and white, the in-house vs. outsource question tends to answer itself."
      }
    ],
    "seo_title": "In-House Billing vs Outsourced Dental RCM: Honest Comparison | ClariDent RCM",
    "seo_description": "In-house dental billing vs outsourced RCM compared honestly: true costs, expertise, turnover risk, control, and a framework to decide which fits your practice."
  }
];

export const getPostBySlug = (slug) => posts.find((p) => p.slug === slug);
